/**
 * StylePicker — compact chip + custom-text picker for the project's universal
 * "Estilo visual" (projects.mangaStyle). The chosen clause is appended by the
 * server to every AI image prompt (bible reference sheets, scene illustrations,
 * covers, manga panels) so the whole project keeps a consistent look.
 */
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';
import { Input } from './ui/input';
import { Label } from './ui/label';

export interface StylePreset {
  id: string;
  clause: string;
  defaultLabel: string;
}

export const PROJECT_STYLE_PRESETS: StylePreset[] = [
  {
    id: 'anime',
    clause: 'anime/manga art style, clean linework, cel shading, expressive eyes, vibrant',
    defaultLabel: 'Anime',
  },
  {
    id: 'manga_bw',
    clause: 'black-and-white manga, screentone shading, dynamic ink, high contrast',
    defaultLabel: 'Mangá P&B',
  },
  {
    id: 'realistic',
    clause: 'semi-realistic digital painting, detailed, cinematic lighting',
    defaultLabel: 'Semi-realista',
  },
  {
    id: 'watercolor',
    clause: 'soft watercolor storybook illustration, gentle palette',
    defaultLabel: 'Aquarela',
  },
  {
    id: 'comic',
    clause: 'American comic book style, bold inks, dynamic, vibrant colors',
    defaultLabel: 'HQ americana',
  },
  {
    id: 'noir',
    clause: 'noir ink illustration, heavy shadows, high contrast black and white',
    defaultLabel: 'Noir',
  },
  {
    id: 'painterly',
    clause: 'painterly concept art, rich texture, cinematic',
    defaultLabel: 'Pintura conceitual',
  },
];

interface StylePickerProps {
  value: string;
  onChange: (style: string) => void;
  disabled?: boolean;
}

export function StylePicker({ value, onChange, disabled }: StylePickerProps) {
  const { t } = useTranslation();
  const trimmed = value.trim();

  const isActive = (clause: string) =>
    trimmed.length > 0 && (trimmed === clause || trimmed.includes(clause));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {PROJECT_STYLE_PRESETS.map((preset) => {
          const active = isActive(preset.clause);
          return (
            <button
              key={preset.id}
              type="button"
              disabled={disabled}
              onClick={() => onChange(preset.clause)}
              aria-pressed={active}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                active
                  ? 'border-primary/40 bg-primary/10 text-primary'
                  : 'border-border bg-muted/40 text-muted-foreground hover:border-primary/30 hover:text-foreground',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              {t(`style.${preset.id}`, preset.defaultLabel)}
            </button>
          );
        })}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="style-picker-custom" className="text-xs font-normal text-muted-foreground">
          {t('style.custom', 'Estilo personalizado')}
        </Label>
        <Input
          id="style-picker-custom"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={t(
            'style.customPlaceholder',
            'Descreva o estilo visual, ex.: aquarela sombria, traços grossos...',
          )}
          className="text-sm"
        />
      </div>
    </div>
  );
}
